import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const userData = await apiService.getMe();
          setUser(userData);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Auth check failed:', error);
          // Token might be expired, try to refresh
          try {
            await apiService.refreshToken();
            const userData = await apiService.getMe();
            setUser(userData);
            setIsAuthenticated(true);
          } catch (refreshError) {
            // Refresh failed, logout
            apiService.logout();
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      await apiService.login(email, password);
      const userData = await apiService.getMe();
      setUser(userData);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const register = useCallback(async (email, password, nome, telefone = null) => {
    try {
      await apiService.register(email, password, nome, telefone);
      // Auto-login after registration
      return await login(email, password);
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [login]);

  const logout = useCallback(() => {
    apiService.logout();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  /**
   * Set auth state from OAuth callback
   * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
   */
  const setAuthFromOAuth = useCallback(async (token, refreshToken, name) => {
    localStorage.setItem('access_token', token);
    localStorage.setItem('refresh_token', refreshToken);
    
    try {
      const userData = await apiService.getMe();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to get user after OAuth:', error);
      // Set basic user info from OAuth
      setUser({ nome: name, email: '' });
      setIsAuthenticated(true);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await apiService.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const isAdminMaster = user?.role === 'admin_master';

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdminMaster,
    login,
    register,
    logout,
    refreshUser,
    setAuthFromOAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
